import Cookie, { CookieNames } from './Cookie';
import apiUrls from './apiUrls';
import { hasValue } from './objectUtils';
import { isDateObject, longTimestamp } from './dateUtils';
import Auth from './Auth';

const HTTP_STATUS_CODES = {
    Unauthorized: 401,
    Forbidden: 403,
    NotFound: 404,
    TooManyRequests: 429,
};

const YOUWELL_ERROR_CODES = {
    ElementNotFound: 1078,
    UnknownError: 901,
    NoInternetConnection: 920,
};

export const VERSION_EXPIRED = 'APP/VERSION_EXPIRED';
export const PAGE_NOTFOUND = 'APP/PAGE_NOTFOUND';

const appSettings = {
    version: '',
    appId: '',
};

export const setAppVersion = (version) => {
    appSettings.version = version;
};

export const setAppId = (appId) => {
    appSettings.appId = appId;
};

class FetchError extends Error {
    constructor(url, status, data) {
        super(data);
        this.url = url;
        this.status = status;

        if (!data) {
            this.errorCode = status;
        } else {
            try {
                const json = JSON.parse(data);
                this.errorCode = json.errorCode;
            } catch (e) {
                this.errorCode = status;
            }
        }
    }
}

export const handleErrors = dispatch => (response) => {
    if (!response.ok) {
        return response.text().then(data => Promise.reject(new FetchError(response.url, response.status, data)));
    }

    if (dispatch) {
        const version = response && response.headers && response.headers.get('X-Portal-Version');
        if (version && version !== appSettings.version) {
            dispatch({ type: VERSION_EXPIRED, previous: appSettings.version, newAvailableVersion: version });
        }
    }
    return Promise.resolve(response);
};

export const json = response => (response.status === 204 ? response.text() : response.json());

export const text = response => response.text();

export const blob = (response) => {
    const disposition = response.headers.get('content-disposition');
    const contentType = response.headers.get('content-type');
    const filenameValues = disposition && disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    const filename = filenameValues && filenameValues[1];

    return response.blob().then(blobData => Promise.resolve({ blobData, filename, contentType }));
};

export const jsonHeader = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
};

export const createRequestParams = (requestParamsObject) => {
    if (!hasValue(requestParamsObject)) {
        return '';
    }

    return Object.keys(requestParamsObject).reduce((prev, key) => (hasValue(requestParamsObject[key]) ? `${prev || '?'}${key}=${requestParamsObject[key]}&` : prev), '');
};

export const getErrorMessageOrKey = (error) => {
    if (!error) {
        return YOUWELL_ERROR_CODES.UnknownError;
    }
    if (error.message === 'Failed to fetch'
        || error.message?.includes('Physical connection is not usable')
        || error.message?.includes('The server was not found or was not accessible')) {
        return YOUWELL_ERROR_CODES.NoInternetConnection;
    }

    return error instanceof Error ? (error.errorCode && error.errorCode.toString()) || error.message || '903' : '902';
};

export const dispatchError = (dispatch, type, options, requestId, error, onUnauthorized) => {
    const err = error?.error || error?.errorCode;
    // type is always `${storePrefix}/${REQUEST_FAILED}`

    if (err === HTTP_STATUS_CODES.Unauthorized) {
        dispatch({
            type, options, requestId, error: null,
        });
        if (onUnauthorized) {
            onUnauthorized();
        }
    } else if (err === HTTP_STATUS_CODES.TooManyRequests) {
        dispatch({
            type, options, requestId, error: '921',
        });
    } else if (options?.isPage && (err === YOUWELL_ERROR_CODES.ElementNotFound || err === HTTP_STATUS_CODES.NotFound)) {
        dispatch({
            type, options, requestId, error: null,
        });
        dispatch({ type: PAGE_NOTFOUND });
    } else {
        const errorMessageKey = getErrorMessageOrKey(error);

        dispatch({
            type, options, requestId, error: errorMessageKey,
        });
    }
};

export const downloadBlobAsFile = (filename, contentType, blobData) => {
    // It is necessary to create a new blob object with mime-type explicitly set
    // otherwise only Chrome works like it should
    const newBlob = new Blob([blobData], { type: contentType });

    let downloadFileName = filename?.replace('{timestamp}', longTimestamp()) || 'donwloaded file.t';

    if (contentType?.toLowerCase().indexOf('application/zip') > -1) {
        downloadFileName += '.zip';
    }

    // IE doesn't allow using a blob object directly as link href
    // instead it is necessary to use msSaveOrOpenBlob
    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        window.navigator.msSaveOrOpenBlob(newBlob, downloadFileName);
        return;
    }

    // For other browsers:
    // Create a link pointing to the ObjectURL containing the blob.
    const data = window.URL.createObjectURL(newBlob);
    const link = document.createElement('a');
    link.href = data;
    link.download = downloadFileName;
    link.click();
    setTimeout(() => {
        // For Firefox it is necessary to delay revoking the ObjectURL
        window.URL.revokeObjectURL(data);
    }, 100);
};

// This prevents multiple simultaneous calls to `/refreshToken`
let refreshPromise = null;
const withRefreshToken = async (fetch, refreshUrl) => {
    if (refreshPromise == null) {
        refreshPromise = fetch(`${apiUrls.baseUrl}/${refreshUrl}`, {
            headers: jsonHeader,
            method: 'POST',
        });
    }
    const result = await refreshPromise;
    if (!result.ok) {
        Auth.deauthenticateUser();
    }
    refreshPromise = null;
    return result;
};

const fetchWithDefaults = async (url, { headers, ...options }) => fetch(url, {
    headers: {
        // ...jsonHeader, // not applicable for file-downloads.. Must override in ReduxStoreHelper.fetchFile if set here!
        ...headers,
        'X-APP-ID': appSettings.appId,
        'X-CSRF-TOKEN': Cookie.getValue(CookieNames.csrf),
    },
    credentials: 'include',
    method: 'GET',
    ...options,
});

// Obsolete - used by old redux-stores (ReduxStoreHelper) - use youwellFetch for new code
export const fetchWithRefresh = async (dispatch, url, options) => {
    const response = await fetchWithDefaults(url, options);

    if (response.status === HTTP_STATUS_CODES.Unauthorized) {
        if (response.headers.has('X-REFRESH-TOKEN')) {
            const refresh = await withRefreshToken(fetchWithDefaults, response.headers.get('X-REFRESH-TOKEN'));

            if (refresh.ok) {
                return fetchWithDefaults(url, options);
            }
        }
    }
    return response;
};

export const youwellFetch = async (url, options) => {
    const response = await fetchWithDefaults(url, options || {});

    if (response.status === HTTP_STATUS_CODES.Unauthorized) {
        if (response.headers.has('X-REFRESH-TOKEN')) {
            const refresh = await withRefreshToken(fetchWithDefaults, response.headers.get('X-REFRESH-TOKEN'));

            if (refresh.ok) {
                return fetchWithDefaults(url, options || {});
            }
        }
    }
    return response;
};


const stringValue = (object) => {
    if (isDateObject(object)) {
        return object.toISOString();
    }
    return object;
};

export const mapToQueryString = requestParams => (requestParams
    ? Object.keys(requestParams).reduce((prev, key) => (hasValue(requestParams[key]) ? `${prev || '?'}${key}=${stringValue(requestParams[key])}&` : prev), '')
    : '');
